import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Tag } from 'antd'
import { CheckOutlined, ClockCircleOutlined, CloseOutlined } from '@ant-design/icons'

import { bangkokClock, bangkokDayKey, daysBetween, formatDateTime } from './journeyTime'
import { durationOf, stateKey, wordsFor } from './journeyWords'
import { standingOf } from './afterOrderWords'
import { groupByDay, isOtherOrder, isVisit } from './journeyDays'
import { afterRows, isMostlyTheSame, isNewerOrder, sharedProductsOf } from './afterOrderFacts'
import { StepRow } from './StepRow'
import { VisitRow } from './VisitRow'
import { DayHeaderLine } from './DayHeaderLine'
import {
  Clock,
  EndRowGrid,
  GapLine,
  Muted,
  NewerOrderGrid,
  Panel,
  StepBody,
  StepGrid,
  StepLabel,
  StepLine
} from './journeyStyles'

const SharedProductsTag = ({ shared }) => {
  const { t } = useTranslation('inquiries')
  if (!shared) return null
  return (
    <Tag color={isMostlyTheSame(shared) ? 'warning' : undefined} style={{ marginInlineEnd: 0 }}>
      {shared.shared > 0 ? t('orderJourney.sharedProducts', shared) : t('orderJourney.noSharedProducts')}
    </Tag>
  )
}

const OlderOrderPaymentRow = ({ step, journey, lang, orderIds }) => {
  const { t } = useTranslation('inquiries')
  const number = (step.details || [])[0]
  return (
    <StepGrid>
      <Clock>{bangkokClock(step.first_at)}</Clock>
      <StepBody>
        <StepLine>
          <Tag style={{ marginInlineEnd: 0 }}>{t('orderJourney.otherOrderTag')}</Tag>
          <StepLabel>{wordsFor(journey, step.name, lang)}</StepLabel>
          {number && <Link to={`/inquiries/${number}`}>{number}</Link>}
          <SharedProductsTag shared={sharedProductsOf(step, orderIds)} />
        </StepLine>
        <Muted $small>{t('orderJourney.otherOrderHint')}</Muted>
      </StepBody>
    </StepGrid>
  )
}

const NewerOrderRow = ({ order, journey, lang, orderIds }) => {
  const { t } = useTranslation('inquiries')
  const paid = order.state?.kind === 'paid'
  return (
    <NewerOrderGrid>
      <Clock>{bangkokClock(order.started_at)}</Clock>
      <StepBody>
        <StepLine>
          <strong>{t('orderJourney.newerOrder')}</strong>
          {order.order_number ? (
            <Link to={`/inquiries/${order.order_number}`}>{order.order_number}</Link>
          ) : (
            <span>#{order.order_id}</span>
          )}
          <Muted>
            · {wordsFor(journey, order.first_event, lang)} ·{' '}
            {t('orderJourney.newerOrderAfter', { duration: durationOf(t, journey.order?.started_at, order.started_at) })}
          </Muted>
        </StepLine>
        <StepLine>
          <SharedProductsTag shared={sharedProductsOf(order, orderIds)} />
          <Tag color={paid ? 'success' : undefined} style={{ marginInlineEnd: 0 }}>
            {t('orderJourney.stateNow', { state: wordsFor(journey, stateKey(order.state), lang) })}
          </Tag>
        </StepLine>
      </StepBody>
    </NewerOrderGrid>
  )
}

const EndIcon = ({ endsBy }) => {
  if (endsBy === 'paid') return <CheckOutlined style={{ color: 'var(--journey-success)' }} />
  if (endsBy === 'cancelled') return <CloseOutlined style={{ color: 'var(--journey-muted)' }} />
  return <ClockCircleOutlined style={{ color: 'var(--journey-muted)' }} />
}

const EndRow = ({ journey, lang }) => {
  const { t } = useTranslation('inquiries')
  const after = journey.after_order
  const now = after.ends_by === 'now'
  return (
    <EndRowGrid $paid={after.ends_by === 'paid'}>
      <Clock>{now ? t('orderJourney.now') : bangkokClock(after.to)}</Clock>
      <StepBody>
        <StepLine>
          <EndIcon endsBy={after.ends_by} />
          <span>{standingOf(t, journey, lang)}</span>
        </StepLine>
        {now && <Muted $small style={{ fontWeight: 400 }}>{t('orderJourney.asOf', { date: formatDateTime(after.to, lang) })}</Muted>}
        {after.ends_by === 'days' && <Muted $small style={{ fontWeight: 400 }}>{t('orderJourney.afterNotShown')}</Muted>}
      </StepBody>
    </EndRowGrid>
  )
}

const AfterRow = ({ row, journey, lang, orderIds }) => {
  if (isNewerOrder(row)) return <NewerOrderRow order={row.order} journey={journey} lang={lang} orderIds={orderIds} />
  if (isOtherOrder(row)) return <OlderOrderPaymentRow step={row} journey={journey} lang={lang} orderIds={orderIds} />
  if (isVisit(row)) return <VisitRow visit={row} journey={journey} lang={lang} orderIds={orderIds} />
  return <StepRow step={row} journey={journey} lang={lang} orderIds={orderIds} />
}

const AfterOrderPart = ({ journey, lang, orderIds }) => {
  const { t } = useTranslation('inquiries')
  const after = journey.after_order
  const startedAt = journey.order?.started_at
  const orderDay = bangkokDayKey(startedAt)
  const rows = afterRows(after)
  const days = groupByDay(rows, startedAt).filter((day) => day.steps.length > 0)
  const lastDay = days.length ? days[days.length - 1].dayKey : orderDay
  const endDay = bangkokDayKey(after.to)
  const endGap = after.complete !== false && endDay && lastDay ? daysBetween(lastDay, endDay) - 1 : 0
  const moreNewer = (after.newer_order_count || 0) - (after.newer_orders || []).length

  return (
    <>
      <GapLine>· {t('orderJourney.afterOrder')} ·</GapLine>
      {after.complete === false && (
        <Muted $small>{t('orderJourney.afterIncomplete', { n: (after.steps || []).length })}</Muted>
      )}
      {rows.length === 0 && (
        <Panel>
          <div>{t('orderJourney.afterEmpty', { duration: durationOf(t, startedAt, after.to) })}</div>
          <Muted>{t('orderJourney.emptyHint')}</Muted>
        </Panel>
      )}
      {days.map((day) => (
        <React.Fragment key={day.dayKey}>
          {day.gapBefore > 0 && <GapLine>· {t('orderJourney.gap', { count: day.gapBefore })} ·</GapLine>}
          {day.dayKey !== orderDay && <DayHeaderLine at={day.firstAt} daysBefore={day.daysBefore} lang={lang} />}
          {day.steps.map((row, index) => (
            <AfterRow key={`${row.kind}-${row.first_at}-${index}`} row={row} journey={journey} lang={lang} orderIds={orderIds} />
          ))}
        </React.Fragment>
      ))}
      {moreNewer > 0 && <GapLine>{t('orderJourney.moreNewerOrders', { count: moreNewer })}</GapLine>}
      {endGap > 0 && <GapLine>· {t('orderJourney.gap', { count: endGap })} ·</GapLine>}
      {endDay && endDay !== lastDay && (
        <DayHeaderLine at={after.to} daysBefore={daysBetween(endDay, orderDay)} lang={lang} />
      )}
      <EndRow journey={journey} lang={lang} />
    </>
  )
}

const journeyProps = {
  journey: PropTypes.object.isRequired,
  lang: PropTypes.string.isRequired
}

NewerOrderRow.propTypes = {
  ...journeyProps,
  order: PropTypes.object.isRequired,
  orderIds: PropTypes.instanceOf(Set).isRequired
}
SharedProductsTag.propTypes = { shared: PropTypes.object }
OlderOrderPaymentRow.propTypes = {
  ...journeyProps,
  step: PropTypes.object.isRequired,
  orderIds: PropTypes.instanceOf(Set).isRequired
}
EndIcon.propTypes = { endsBy: PropTypes.string }
EndRow.propTypes = journeyProps
AfterRow.propTypes = {
  ...journeyProps,
  row: PropTypes.object.isRequired,
  orderIds: PropTypes.instanceOf(Set).isRequired
}
AfterOrderPart.propTypes = {
  ...journeyProps,
  orderIds: PropTypes.instanceOf(Set).isRequired
}

export { AfterOrderPart }
